"use client";

import { Fragment, useMemo } from "react";
import { Listbox, Transition } from "@headlessui/react";
import { Season } from "@/types/leaderboards";
import { convertLetters } from "@/utils/letterConversion";
import { ChevronDownIcon } from "@heroicons/react/20/solid";

export interface SeasonSelectDropdownProps {
  seasons: Season[];
  selectedSeasonId: string;
  onSeasonChange: (seasonId: string) => void;
}

export function SeasonSelectDropdown({
  seasons,
  selectedSeasonId,
  onSeasonChange,
}: SeasonSelectDropdownProps) {
  const selectedSeason = useMemo(
    () => seasons.find((s) => s.id === selectedSeasonId),
    [seasons, selectedSeasonId]
  );

  return (
    <div className="relative inline-block min-w-[295px]">
      <Listbox value={selectedSeasonId} onChange={onSeasonChange}>
        {({ open }) => (
          <>
            <Listbox.Button className="flex w-full items-center gap-2 rounded-lg border-4 border-brand bg-brand_charcoal py-2 px-4 text-brand text-left font-prviking text-xl sm:text-2xl">
              {selectedSeason && (
                <>
                  {convertLetters(selectedSeason.name)}
                  {selectedSeason.isActive ? " (Active)" : ""}
                </>
              )}
              <ChevronDownIcon className="w-5 h-5 text-brand" />
            </Listbox.Button>

            <Transition
              as={Fragment}
              show={open}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Listbox.Options className="absolute z-50 mt-1 w-full min-w-[260px] max-h-60 overflow-auto rounded-lg border-4 border-brand bg-brand_charcoal py-1 text-brand text-left font-prviking text-xl sm:text-2xl shadow-lg">
                {seasons.map((season) => (
                  <Listbox.Option
                    key={season.id}
                    value={season.id}
                    className={({ active }) =>
                      `cursor-pointer select-none px-4 py-2 ${
                        active ? "bg-brand text-black" : ""
                      }`
                    }
                  >
                    {convertLetters(season.name)}
                    {season.isActive ? " (Active)" : ""}
                  </Listbox.Option>
                ))}
              </Listbox.Options>
            </Transition>
          </>
        )}
      </Listbox>
    </div>
  );
}
